import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { AppData, BBQSubmission } from '../App.js';
import Calendar, {
  BOAT_CAPACITY,
  boatsLeftForTime,
  hourMinute,
  isoDate,
  optionsSchema,
  type Option,
} from './Calendar';
import Modal, { type Calculation } from './Modal';
import { classes } from '../utils';
import Pflicht from './Pflicht';
import {
  getSeason,
  maxPeoplePerSlot,
  gluehweinHint,
  priceLabel,
  WINTER_DRINKS_HINT,
  WINTER_GROUP_HEADLINES,
  WINTER_PACKAGES,
} from '../season';

interface Configurator {
  calculations: Calculation[];
  onNext: () => void;
  people: number;
  loc: 'hanekenfaehr' | 'alter_hafen';
}

const MIN_PEOPLE = 4;

export default function Configurator({
  calculations,
  onNext,
  people,
  loc,
}: Configurator) {
  const appData: AppData = (window as any).bbqData;
  if (!appData) return null;

  const { register, handleSubmit, setValue, watch } =
    useFormContext<BBQSubmission>();
  const date = watch('date');
  const isThursday = date ? new Date(date).getDay() === 4 : false;
  const isMoWe = date
    ? new Date(date).getDay() <= 3 && new Date(date).getDay() >= 1
    : false;
  const isWinter = getSeason(date) === 'winter';
  // Mon–Wed Gruppenangebot is a summer-only offer
  const isEssen = !isWinter && !!watch('packages.essen');
  const counterPeople = Math.floor(watch('personCount'));
  const counterChildren = Math.floor(watch('childrenCount'));
  const watchTime = watch('time');

  // Person capacity per time slot: `?personen=`, capped Nov–Mar to the usable
  // BBQ donuts (BBQ-Optionen → Winter) — see season.ts
  const capacity = maxPeoplePerSlot(date, people, appData);

  // Bookings per date, fetched here (rather than inside Calendar) so the person
  // slider and the time slots below it share the same availability data.
  const [taken, setTaken] = useState<Map<string, Option[]>>(new Map());

  const fetchOptionsFor = (d: string, location: string) => {
    const url = new URL(
      '?' + new URLSearchParams({ date: d, location }),
      appData.getUrl
    );

    return fetch(url)
      .then((res) => res.json())
      .then((json) =>
        setTaken((o) => {
          const next = new Map(o);
          next.set(d, optionsSchema.parse(json));
          return next;
        })
      );
  };

  useEffect(() => {
    if (date && !taken.has(date)) {
      fetchOptionsFor(date, loc);
    }
  }, [date]);

  // Capacity for the currently selected time slot: every existing booking is
  // rounded up to a whole boat, summed, and subtracted from the location's total
  // person capacity (`capacity` above). Only whole boats' worth of that remainder
  // can actually be booked, so the slider is capped to that (not the raw remainder).
  const selectedTaken = taken.get(isoDate(new Date(date)));
  const hasOtherBookings =
    !!selectedTaken &&
    !!watchTime &&
    selectedTaken.some((b) => hourMinute(b.bookingDate) === watchTime);
  const boatsLeft =
    selectedTaken && watchTime
      ? boatsLeftForTime(selectedTaken, watchTime, capacity)
      : Math.floor(capacity / BOAT_CAPACITY);
  const remaining = boatsLeft * BOAT_CAPACITY;
  const sliderMax = Math.max(MIN_PEOPLE, remaining);

  // If the chosen slot leaves fewer spots than the current selection, pull the
  // person count down to what still fits there.
  useEffect(() => {
    if (Math.floor(watch('personCount')) > remaining) {
      setValue('personCount', Math.max(MIN_PEOPLE, remaining));
    }
  }, [remaining]);

  useEffect(() => {
    if (isMoWe) {
      setValue('packages.Grill', false);
      setValue('packages.Grillgut', false);
      setValue('packages.Kaffee & Kuchen', false);
    } else {
      setValue('packages.essen', false);
    }
  }, [isMoWe]);

  useEffect(() => {
    if (isEssen) {
      setValue('packages.Getränke (2 Kisten)', true);
    }
  }, [isEssen]);

  return (
    <form
      style={{ minHeight: 400 }}
      onSubmit={handleSubmit(onNext, (errors) => {
        const errField = Object.keys(errors)[0] as
          | keyof typeof errors
          | undefined;
        if (errField === 'date' || errField === 'time') {
          document.querySelector('.cf-cal-left')?.scrollIntoView({
            behavior: 'instant',
            block: 'center',
          });
        }
      })}
      className={'list-center'}>
      <h2 className={'headline'} style={{ maxWidth: 450 }}>
        Wann möchtest du Deinen BBQ Donut buchen?*
      </h2>
      <p className={'section-end'}>{appData.startDesc}</p>

      <Calendar loc={loc} maxPeople={capacity} taken={taken} />
      <div className={'section-end'} />

      {/* Personen */}
      <h2 className={'headline'}>Personenanzahl*</h2>
      <div
        className={classes(
          'tw:w-full tw:max-w-[500px]', // tw:has-invalid:outline-8
          !isThursday && 'section-end'
        )}>
        {capacity > 10 ? (
          <div className={'tw:flex tw:items-center'}>
            <input
              type={'range'}
              className={'tw:w-full slider'}
              min={MIN_PEOPLE}
              max={sliderMax}
              // step={0.01}
              defaultValue={watch('personCount')}
              {...register('personCount')}
            />
            <p className={'tw:w-16 tw:text-lg tw:font-bold tw:m-0'}>
              {counterPeople}
            </p>
          </div>
        ) : (
          <div className={'radio-list'}>
            {new Array(Math.max(0, sliderMax - (MIN_PEOPLE - 1)))
              .fill(null)
              .map((_, i) => (
                <input
                  key={i}
                  type="radio"
                  value={i + MIN_PEOPLE}
                  data-label={i + MIN_PEOPLE}
                  className={'radio invalid:outline-8'}
                  required
                  {...register('personCount')}
                />
              ))}
          </div>
        )}
        {hasOtherBookings && remaining > 0 ? (
          <p className={'tw:mt-3 tw:text-sm tw:text-white tw:font-bold'}>
            {`Begrenzte Verfügbarkeit zu dieser Uhrzeit – noch ${remaining} ${remaining === 1 ? 'Platz' : 'Plätze'} frei.`}
          </p>
        ) : null}
      </div>

      <label
        className={classes(
          'checkbox-container-bold section-end',
          !isThursday && 'tw:hidden'
        )}>
        <input
          type="checkbox"
          className={'checkbox'}
          {...register('isStudent')}
        />
        Ich bin Studi.
      </label>

      {/* Kinder */}
      <h2 className={'headline'}>Davon Kinder*</h2>
      <div className={'section-end tw:w-full tw:max-w-[500px]'}>
        {capacity > 10 ? (
          <div className={'tw:flex tw:items-center'}>
            <input
              type={'range'}
              className={'tw:w-full slider'}
              min={0}
              max={capacity - 2}
              // step={0.01}
              defaultValue={watch('childrenCount')}
              {...register('childrenCount')}
            />
            <p className={'tw:w-16 tw:text-lg tw:font-bold tw:m-0'}>
              {counterChildren}
            </p>
          </div>
        ) : (
          <div className={'radio-list'}>
            {new Array(capacity - 1).fill(null).map((_, i) => (
              <input
                key={i}
                type="radio"
                value={i}
                className={'radio'}
                data-label={i}
                required
                {...register('childrenCount')}
              />
            ))}
          </div>
        )}
      </div>

      {/* Wahlen */}
      <div className={'tw:hidden'}>
        <h2 className={'headline'}>Boot oder Floß*</h2>
        <div className={'radio-list section-end'}>
          {appData.vehicles.map((vehicle) =>
            vehicle.available ? (
              <input
                style={{
                  width: 300,
                }}
                key={vehicle.name}
                type="radio"
                className={'radio'}
                value={vehicle.name}
                data-label={vehicle.name}
                required
                {...register('vehicleId')}
              />
            ) : null
          )}
        </div>
      </div>

      {isWinter ? (
        /* Winter (Nov–Feb): fixed packages from season.ts */
        (['drinks', 'food'] as const).map((group) => (
          <React.Fragment key={group}>
            <h2 className={'headline'}>{WINTER_GROUP_HEADLINES[group]}</h2>
            <div className={'radio-list'}>
              {WINTER_PACKAGES.filter((p) => p.group === group).map((p) => (
                <input
                  key={p.key}
                  type="checkbox"
                  className={'radio tw:w-auto tw:px-10'}
                  value={p.name}
                  data-label={p.name}
                  {...register(`packages.${p.key}`)}
                />
              ))}
            </div>
            <ul className={'tw:mt-4 tw:text-sm tw:opacity-70 tw:text-center'}>
              {WINTER_PACKAGES.filter((p) => p.group === group).map((p) => (
                <li key={p.key}>
                  {p.name}: {priceLabel(p)}
                  {p.key === 'winter_gluehwein'
                    ? ` · ${gluehweinHint(counterPeople, BOAT_CAPACITY)}`
                    : null}
                </li>
              ))}
            </ul>
            <div className={'section-end'}></div>
          </React.Fragment>
        ))
      ) : (
        <>
          <h2 className={'headline'}>Ausstattung</h2>
          <div className={'radio-list'}>
            {appData.packages.map((p) => {
              if (p.optional) return;

              return (
                <input
                  key={p.name}
                  type="checkbox"
                  className={'radio tw:w-auto tw:px-10'}
                  value={p.name}
                  disabled={(p.name === 'Grill' && isMoWe) || isEssen}
                  data-label={p.name}
                  {...register(`packages.${p.name}`)}
                />
              );
            })}
          </div>
          {isMoWe ? (
            <p className={'tw:mt-4 tw:text-sm tw:opacity-70'}>
              *Einige Pakete sind nur Do-So verfügbar.
            </p>
          ) : null}
          <div className={'section-end'}></div>

          <h2 className={'headline'}>Optionale Pakete</h2>
          <div className={'radio-list'}>
            {appData.packages.map((p) => {
              if (!p.optional) return;

              return (
                <input
                  key={p.name}
                  type="checkbox"
                  className={'radio tw:w-auto tw:px-10'}
                  value={p.name}
                  disabled={
                    isMoWe && ['Kaffee & Kuchen', 'Grillgut'].includes(p.name)
                  }
                  data-label={p.name}
                  {...register(`packages.${p.name}`)}
                />
              );
            })}
            {isMoWe ? (
              <input
                key={'essen'}
                type="checkbox"
                className={'radio tw:w-auto tw:px-10'}
                value={'essen'}
                data-label={'Gruppenangebot'}
                {...register(`packages.essen`)}
              />
            ) : null}
          </div>
          {isMoWe ? (
            <p className={'tw:mt-4 tw:text-sm tw:opacity-70'}>
              *Einige Pakete sind nur Do-So verfügbar.
            </p>
          ) : (
            <p className={'tw:mt-4'}>{appData.packagesDesc}</p>
          )}
          <div className={'section-end'}></div>
        </>
      )}

      {/* Getränke — free-text wish in summer, fixed hint in the Christmas program */}
      <h2 className={'headline'}>Getränkewunsch</h2>
      {isWinter ? (
        <p className={'section-end'}>{WINTER_DRINKS_HINT}</p>
      ) : (
        <>
          <p>{appData.drinkDesc}</p>
          <textarea
            className={'text-field section-end'}
            rows={5}
            placeholder={appData.drinkPlaceholder}
            {...register('drinks')}
          />
        </>
      )}

      <h2 className={'headline'}>Notizen</h2>
      <textarea
        className={'text-field section-end'}
        rows={5}
        placeholder={'Notizen'}
        {...register('userNotes')}
      />

      {isEssen ? (
        <div className={'tw:w-full'}>
          <h2 className={'headline'}>Gruppenangebot</h2>
          <p
            dangerouslySetInnerHTML={{
              __html: (loc === 'hanekenfaehr'
                ? appData.hanekenfaehrInfo
                : appData.alter_hafenInfo
              ).replace(/\n/g, '<br>'),
            }}
            className={'tw:w-full tw:py-2'}></p>
          <textarea
            className={'text-field section-end tw:w-full'}
            rows={5}
            placeholder={'z. B. 3x Bacon Royal Burger, 2x Vegetarische Lasagne'}
            {...register('foodChoice')}
          />
        </div>
      ) : null}

      <button className={'button'}>Weiter</button>

      <Pflicht />
    </form>
  );
}
